import { Select, Spin, Tooltip } from "antd";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { SharepointApi } from "../types/sharepointApi";
import { getFileIcon } from "../utils/fileIcons";

type SharepointSearchProps = {
	api: SharepointApi;
	tenantKey?: string;
	containerId?: string;
	rootFolderId?: string;
	keyId?: string;
	placeholder?: string;
	style?: React.CSSProperties;
	disabled?: boolean;
};

type SearchResult = {
	item_id: string;
	name: string;
	item_type: "file" | "folder";
	folder_hierarchy?: string[];
	owner_email?: string;
	createdAt?: string;
	updatedAt?: string;
};

// Cache for folder names to avoid repeated API calls
const folderNameCache: Record<string, string> = {};

function SharepointSearch(props: SharepointSearchProps) {
	const location = useLocation();

	const [options, setOptions] = useState<any[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [searchText, setSearchText] = useState<string | null>(null);
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const [folderNames, setFolderNames] = useState<Record<string, string>>({});
	const [searchResults, setSearchResults] = useState<SearchResult[]>([]);

	const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const abortControllerRef = useRef<AbortController | null>(null);

	const { api, tenantKey, containerId, keyId } = props;

	/**
	 * Fetch folder names for the given folder IDs
	 */
	const fetchFolderNames = useCallback(
		async (folderIds: string[]) => {
			if (!tenantKey || !containerId || folderIds.length === 0) return;

			const uniqueIds = folderIds.filter(
				(id) => !folderNameCache[id] && !folderNames[id]
			);
			if (uniqueIds.length === 0) return;

			const newNames: Record<string, string> = {};

			await Promise.all(
				uniqueIds.map(async (folderId) => {
					try {
						const response: any = await api.getFile(folderId, containerId);
						const name = response?.name || folderId;
						folderNameCache[folderId] = name;
						newNames[folderId] = name;
					} catch (error) {
						// If fetch fails, use folder ID as name
						newNames[folderId] = folderId;
					}
				})
			);

			setFolderNames((prev) => ({ ...prev, ...newNames }));
		},
		[api, tenantKey, containerId, folderNames]
	);

	/**
	 * Perform server-side search
	 */
	const performSearch = useCallback(
		async (query: string) => {
			if (!tenantKey || !query.trim() || !keyId) {
				setOptions([]);
				return;
			}

			// Cancel previous request if exists
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}
			abortControllerRef.current = new AbortController();

			setIsLoading(true);

			try {
				const response: any = await api.search(query.trim(), 20);
				const results: SearchResult[] = response?.results || [];

				// Collect all folder IDs that need names
				const allFolderIds: string[] = [];
				results.forEach((item) => {
					if (item.folder_hierarchy && item.folder_hierarchy.length > 0) {
						// Skip root folder (first item), only get subfolder IDs
						const subFolders = item.folder_hierarchy.slice(1);
						allFolderIds.push(...subFolders);
					}
				});

				// Store raw results
				setSearchResults(results);

				// Fetch folder names
				if (allFolderIds.length > 0) {
					await fetchFolderNames([...new Set(allFolderIds)]);
				}

				// Format options after folder names are fetched
				const formattedOptions = results.map((item) => ({
					value: item.item_id,
					label: renderOptionLabel(item),
					file: item,
				}));

				setOptions(formattedOptions);
				setIsDropdownOpen(true);
			} catch (error: any) {
				// Don't show error for aborted requests
				if (error.name === "AbortError" || error.message === "canceled") {
					return;
				}
				console.error("Search error:", error);
				setOptions([]);
			} finally {
				setIsLoading(false);
			}
		},
		[api, tenantKey, keyId, fetchFolderNames]
	);

	// Build folder path with names, excluding root folder
	const buildFolderPath = (file: SearchResult): string => {
		if (!file.folder_hierarchy || file.folder_hierarchy.length === 0) {
			return "Root";
		}

		// Skip the root folder (first item in hierarchy)
		const subFolders = file.folder_hierarchy.slice(1);

		if (subFolders.length === 0) {
			return "Root";
		}

		// Map folder IDs to names
		const pathParts = subFolders.map(
			(id) => folderNameCache[id] || folderNames[id] || id
		);

		return pathParts.join(" / ");
	};

	// Re-format options when folder names are updated
	useEffect(() => {
		if (searchResults.length > 0) {
			const formattedOptions = searchResults.map((item) => ({
				value: item.item_id,
				label: renderOptionLabel(item),
				file: item,
			}));
			setOptions(formattedOptions);
		}
	}, [folderNames, searchResults]);

	// Render option label with icon and folder path
	const renderOptionLabel = (file: SearchResult) => {
		const icon = getFileIcon(file.name, file.item_type === "folder");
		const folderPath = buildFolderPath(file);

		return (
			<div
				style={{
					display: "flex",
					flexDirection: "row",
					alignItems: "center",
					gap: 12,
				}}
			>
				<div
					style={{
						width: 24,
						display: "flex",
						justifyContent: "center",
						flexShrink: 0,
					}}
				>
					{icon}
				</div>
				<div style={{ minWidth: 0, flex: 1 }}>
					<div
						style={{
							fontWeight: 600,
							whiteSpace: "nowrap",
							overflow: "hidden",
							textOverflow: "ellipsis",
						}}
					>
						{file.name}
					</div>
					<Tooltip title={folderPath}>
						<div
							style={{
								fontSize: 12,
								color: "#888",
								whiteSpace: "nowrap",
								overflow: "hidden",
								textOverflow: "ellipsis",
							}}
						>
							{folderPath}
						</div>
					</Tooltip>
				</div>
			</div>
		);
	};

	// Handle search input with debouncing
	const handleSearch = (text: string) => {
		setSearchText(text);

		// Clear previous timer
		if (debounceTimer.current) {
			clearTimeout(debounceTimer.current);
		}

		const query = text.trim();

		// Close dropdown if query is empty
		if (!query) {
			setOptions([]);
			setIsDropdownOpen(false);
			return;
		}

		// Debounce search for 300ms
		debounceTimer.current = setTimeout(() => {
			performSearch(query);
		}, 300);
	};

	// Handle option selection - navigate to preview page
	const handleSelect = (selectedValue: string) => {
		if (!containerId || !selectedValue) return;

		window.open(
			api.previewPath(selectedValue, containerId, location.pathname),
			"_blank"
		);

		// Clear the search text and close dropdown immediately after selection
		setSearchText("");
		setOptions([]);
		setSearchResults([]);
		setIsDropdownOpen(false);
	};

	// Determine if component should be disabled
	const isDisabled = useMemo(() => {
		if (props.disabled) return true;
		return !tenantKey || !containerId || !props.keyId;
	}, [props.disabled, tenantKey, containerId, props.keyId]);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			if (debounceTimer.current) {
				clearTimeout(debounceTimer.current);
			}
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}
		};
	}, []);

	return (
		<Select
			showSearch
			disabled={isDisabled}
			placeholder={
				isLoading ? "Searching..." : props.placeholder || "Search files..."
			}
			style={{ width: 300, ...props.style }}
			filterOption={false}
			onSearch={handleSearch}
			searchValue={searchText ?? ""}
			onSelect={(value: any) => handleSelect(value as string)}
			options={options}
			// Always set value to null so selected value is never displayed
			// The field will appear empty after selection
			value={null}
			notFoundContent={
				isLoading ? (
					<div style={{ padding: "8px 12px", textAlign: "center" }}>
						<Spin size="small" />
						<div style={{ marginTop: 4, fontSize: 12, color: "#888" }}>
							Searching...
						</div>
					</div>
				) : searchText?.trim() ? (
					<div
						style={{ padding: "8px 12px", textAlign: "center", color: "#888" }}
					>
						No files found
					</div>
				) : null
			}
			suffixIcon={null}
			onClear={() => {
				setSearchText("");
				setOptions([]);
				setSearchResults([]);
				setIsDropdownOpen(false);
				if (debounceTimer.current) {
					clearTimeout(debounceTimer.current);
				}
			}}
		/>
	);
}

export default SharepointSearch;
